import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getQrCodes, createQrCode, deleteQrCode, downloadQrCode, previewQrCode, getMenus } from "@/api/endpoints";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Plus, Trash2, Download, Copy, QrCode, Check } from "lucide-react";

function QrPreview({ qrId }: { qrId: string }) {
  const [src, setSrc] = useState("");

  useEffect(() => {
    let url = "";
    previewQrCode(qrId).then((blob: Blob) => {
      url = URL.createObjectURL(blob);
      setSrc(url);
    }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [qrId]);

  if (!src) return <div className="flex size-40 items-center justify-center rounded-lg bg-muted"><QrCode className="size-8 text-muted-foreground" /></div>;
  return <img src={src} alt="QR Code" className="size-40 rounded-lg" />;
}

export function QrCodesPage() {
  const { venueId } = useParams<{ venueId: string }>();
  const qc = useQueryClient();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [menuId, setMenuId] = useState("");
  const [fgColor, setFgColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#FFFFFF");
  const [withLogo, setWithLogo] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);

  const { data: qrCodes, isLoading } = useQuery({
    queryKey: ["qrCodes", venueId],
    queryFn: () => getQrCodes(venueId!),
    enabled: !!venueId,
  });

  const { data: menus } = useQuery({
    queryKey: ["menus", venueId],
    queryFn: () => getMenus(venueId!),
    enabled: !!venueId,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createQrCode(venueId!, {
        label: label || undefined,
        menuId: menuId || undefined,
        style: (fgColor !== "#000000" || bgColor !== "#FFFFFF" || !withLogo) ? { fgColor, bgColor, withLogo } : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["qrCodes", venueId] });
      setOpen(false);
      setLabel("");
      setMenuId("");
      setFgColor("#000000");
      setBgColor("#FFFFFF");
      setWithLogo(true);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteQrCode,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["qrCodes", venueId] }),
  });

  const handleDownload = async (id: string, shortUrl: string, format: "png" | "svg") => {
    const blob = await downloadQrCode(id, format);
    const url = URL.createObjectURL(blob as Blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-${shortUrl}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopy = (shortUrl: string) => {
    navigator.clipboard.writeText(`https://heremenu.app/q/${shortUrl}`);
    setCopied(shortUrl);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("qr_codes_title")}</h1>
        <Button onClick={() => setOpen(true)}>
          <Plus className="mr-2 size-4" />
          {t("new_qr")}
        </Button>
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-muted-foreground">{t("loading")}</div>
      ) : qrCodes?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <QrCode className="mb-4 size-12 text-muted-foreground" />
          <p className="text-muted-foreground">{t("no_qr")}</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {qrCodes?.map((qr) => (
            <Card key={qr.id}>
              <CardContent className="pt-6">
                <div className="mb-4 flex justify-between">
                  <div className="flex gap-4">
                    <QrPreview qrId={qr.id} />
                    <div>
                      <p className="font-medium">{qr.label || "QR Kod"}</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        heremenu.app/q/{qr.shortUrl}
                      </p>
                    </div>
                  </div>
                  <ConfirmDialog
                    trigger={
                      <Button variant="ghost" size="icon" className="size-8">
                        <Trash2 className="size-4 text-destructive" />
                      </Button>
                    }
                    title={t("delete_qr")}
                    description={t("delete_qr_desc")}
                    confirmLabel={t("delete")}
                    onConfirm={() => deleteMutation.mutate(qr.id)}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopy(qr.shortUrl)}
                  >
                    <span className="mr-1 inline-flex size-4 items-center justify-center">
                      {copied === qr.shortUrl ? <Check className="size-4 text-primary" /> : <Copy className="size-4" />}
                    </span>
                    <span>{copied === qr.shortUrl ? t("copied") : t("copy_url")}</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(qr.id, qr.shortUrl, "png")}
                  >
                    <Download className="mr-1 size-4" />
                    PNG
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(qr.id, qr.shortUrl, "svg")}
                  >
                    <Download className="mr-1 size-4" />
                    SVG
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create QR Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("create_new_qr")}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createMutation.mutate();
            }}
            className="space-y-4"
          >
            <div>
              <Label>{t("label_optional")}</Label>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={t("label_placeholder")}
              />
            </div>
            <div>
              <Label>{t("target_menu")}</Label>
              <Select value={menuId || "all"} onValueChange={(v) => setMenuId(v === "all" ? "" : (v ?? ""))}>
                <SelectTrigger>
                  <SelectValue placeholder={t("all_menus")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("all_menus")}</SelectItem>
                  {menus?.map((menu) => (
                    <SelectItem key={menu.id || (menu as unknown as Record<string, string>)._id} value={menu.id || (menu as unknown as Record<string, string>)._id}>
                      {menu.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 rounded-lg border p-3">
              <Switch checked={withLogo} onCheckedChange={setWithLogo} />
              <Label>{t("show_logo_in_qr")}</Label>
            </div>
            <div>
              <Label>{t("colors")}</Label>
              <div className="mt-1 flex gap-4">
                <div className="flex items-center gap-2">
                  <input type="color" value={fgColor} onChange={(e) => setFgColor(e.target.value)} className="size-9 cursor-pointer rounded border" />
                  <span className="text-sm text-muted-foreground">{t("dot_color")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="size-9 cursor-pointer rounded border" />
                  <span className="text-sm text-muted-foreground">{t("bg")}</span>
                </div>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={createMutation.isPending}>
              {createMutation.isPending ? t("creating") : t("create_qr")}
            </Button>
            {createMutation.isError && (
              <p className="text-sm text-destructive">
                {t("qr_limit")}
              </p>
            )}
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
